import { useSearchParams } from 'react-router-dom';
import { encodeInputState, initInputState } from 'src/actuators/user-input/InitInputState.ts';
import { UserInput } from 'src/actuators/user-input/types.ts';
import { useContext, useEffect, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { ActuatorsAppUserInputPageData } from 'src/actuators/user-input/userInputPageMapping.ts';
import { ActuatorTypes, QueryParams } from 'src/actuators/user-input/calculations/constants.ts';
import { ORDER_BY_NUMBER_QUERY_PARAM, ORDER_BY_STRING_QUERY_PARAM } from 'src/modules/common/constants.ts';
import { FilterConfig } from 'src/utils/composeFilter.ts';
import { LocationStateContextType, initLocationState } from 'src/actuators/locationStateContext.ts';
import { useLocationState } from 'src/actuators/user-input/useLocationState.ts';
import { calculateUserInputResult } from './utils/calculateUserInputResult';

export function useUserInputState(
    selectedActuatorType: ActuatorTypes,
    initialState: UserInput,
    config: FilterConfig,
    data?: ActuatorsAppUserInputPageData,
): {
        state: UserInput;
        searchParams: URLSearchParams;
        setState: (newState: UserInput | null) => void;
        locationState: LocationStateContextType['locationState'];
        setLocationState: LocationStateContextType['setLocationState'];
    } {
    const { userSettings } = useContext(ConfigContext);
    const [locationState, setLocationState] = useLocationState();
    const [searchParams, setSearchParams] = useSearchParams();

    const state = useMemo(() => initInputState(
        searchParams,
        initialState,
    ), [searchParams, initialState]);

    const setState = (input: UserInput | null) => {
        setLocationState({ ...initLocationState });
        searchParams.delete(ORDER_BY_NUMBER_QUERY_PARAM);
        searchParams.delete(ORDER_BY_STRING_QUERY_PARAM);

        config.forEach(([name]) => {
            searchParams.delete(name);
        });

        searchParams.delete(QueryParams.input);
        searchParams.delete(QueryParams.labels);
        searchParams.delete(QueryParams.forces);
        searchParams.delete(QueryParams.torques);

        if (input !== null) {
            const newInputState = calculateUserInputResult({
                inputState: input,
                data,
                searchParams,
                selectedActuatorType,
                currentRegion: userSettings.currentRegion,
            });
            searchParams.set(QueryParams.input, encodeInputState(newInputState));
        }

        setSearchParams(searchParams, { replace: true, state: locationState });
    };

    useEffect(() => {
        const prevQuery = searchParams.toString();
        const newInputState = calculateUserInputResult({
            inputState: state,
            data,
            searchParams,
            selectedActuatorType,
            currentRegion: userSettings.currentRegion,
        });
        searchParams.set(QueryParams.input, encodeInputState(newInputState));
        if (prevQuery !== searchParams.toString()) {
            setSearchParams(searchParams, { replace: true, state: locationState });
        }
    }, [data, locationState, searchParams, setSearchParams, selectedActuatorType, state, userSettings.currentRegion]);

    return { state, searchParams, setState, locationState, setLocationState };
}
